/**
 * cryptoUtil.ts
 * WebCrypto Wrapper for E2EE Chat (LMS Edu-JALM v1.5)
 * Requirements: Zero-knowledge, non-extractable keys once decrypted, AES-GCM, RSA-OAEP.
 */

const SALT = new TextEncoder().encode('LMS_EDU_JALM_STATIC_SALT_15'); // Basic salt for PBKDF2
const ITERATIONS = 100000;

export interface E2EEKeyPairStrings {
  publicKey: string;
  encryptedPrivateKey: string;
  signingPublicKey?: string;
  encryptedSigningPrivateKey?: string;
}

// Helper to convert ArrayBuffer to Base64
const bufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

// Helper to convert Base64 to ArrayBuffer
const base64ToBuffer = (base64: string): ArrayBuffer => {
  if (!base64) return new ArrayBuffer(0);
  
  let clean = base64;
  // Handle PEM format (strip headers/footers)
  if (base64.includes('-----')) {
    clean = base64.replace(/-----BEGIN [^-]+-----/g, '')
                 .replace(/-----END [^-]+-----/g, '');
  }

  // Standardize Web-Safe Base64 and strip all non-base64 characters
  const sanitized = clean
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .replace(/[^A-Za-z0-9+/=]/g, '');
  
  try {
    const binary = window.atob(sanitized);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  } catch (err) {
    console.error("Failed to decode Base64 string:", sanitized.substring(0, 30) + "...");
    throw err;
  }
};

/**
 * Derives an AES-GCM key from the user's password using PBKDF2.
 */
const deriveKeyFromPassword = async (password: string): Promise<CryptoKey> => {
  const enc = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: SALT,
      iterations: ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

export const generateKeyPair = async (): Promise<CryptoKeyPair> => {
  return window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt']
  );
};

/**
 * Generates an RSA-PSS Key Pair for digital signatures.
 */
export const generateSigningKeyPair = async (): Promise<CryptoKeyPair> => {
  return window.crypto.subtle.generateKey(
    {
      name: 'RSA-PSS',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['sign', 'verify']
  );
};

/**
 * Encrypts a newly generated private key with a password.
 * Returns the Base64-encoded encrypted private key along with the IV (prepended).
 */
export const encryptPrivateKey = async (privateKey: CryptoKey, password: string): Promise<string> => {
  const exportedPrivateKey = await window.crypto.subtle.exportKey('pkcs8', privateKey);
  const aesKey = await deriveKeyFromPassword(password);
  
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    exportedPrivateKey
  );

  // Combine IV and Ciphertext
  const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encryptedBuffer), iv.length);

  return bufferToBase64(combined.buffer);
};

/**
 * Unlocks an encrypted private key and returns a non-extractable session CryptoKey.
 * Supports both RSA-OAEP (encryption) and RSA-PSS (signing).
 */
export const decryptPrivateKey = async (
  encryptedPrivateKeyBase64: string, 
  password: string,
  algorithm: 'RSA-OAEP' | 'RSA-PSS' = 'RSA-OAEP'
): Promise<CryptoKey> => {
  const aesKey = await deriveKeyFromPassword(password);
  const combinedBuffer = base64ToBuffer(encryptedPrivateKeyBase64);
  
  const iv = new Uint8Array(combinedBuffer.slice(0, 12));
  const encryptedMaterial = combinedBuffer.slice(12);

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    encryptedMaterial
  );

  return window.crypto.subtle.importKey(
    'pkcs8',
    decryptedBuffer,
    {
      name: algorithm,
      hash: 'SHA-256',
    },
    false,
    algorithm === 'RSA-OAEP' ? ['decrypt'] : ['sign']
  );
};

/**
 * Exports a Public Key to SPKI Base64 format for database storage.
 */
export const exportPublicKey = async (publicKey: CryptoKey): Promise<string> => {
  const exported = await window.crypto.subtle.exportKey('spki', publicKey);
  return bufferToBase64(exported);
};

/**
 * Imports a Public Key from SPKI Base64 format into a usable CryptoKey.
 * Supports both RSA-OAEP (encryption) and RSA-PSS (signing).
 */
export const importPublicKey = async (
  publicKeyBase64: string,
  algorithm: 'RSA-OAEP' | 'RSA-PSS' = 'RSA-OAEP'
): Promise<CryptoKey> => {
  const buffer = base64ToBuffer(publicKeyBase64);
  return window.crypto.subtle.importKey(
    'spki',
    buffer,
    {
      name: algorithm,
      hash: 'SHA-256',
    },
    true,
    algorithm === 'RSA-OAEP' ? ['encrypt'] : ['verify']
  );
};

export const createAndPackageUserKeys = async (password: string): Promise<E2EEKeyPairStrings> => {
  // 1. Encryption Keys (RSA-OAEP)
  const encKeyPair = await generateKeyPair();
  const encryptedPrivateKey = await encryptPrivateKey(encKeyPair.privateKey, password);
  const publicKeyStr = await exportPublicKey(encKeyPair.publicKey);

  // 2. Signing Keys (RSA-PSS) - Pillar 4
  const sigKeyPair = await generateSigningKeyPair();
  const encryptedSigningPrivateKey = await encryptPrivateKey(sigKeyPair.privateKey, password);
  const signingPublicKeyStr = await exportPublicKey(sigKeyPair.publicKey);
  
  return {
    publicKey: publicKeyStr,
    encryptedPrivateKey,
    signingPublicKey: signingPublicKeyStr,
    encryptedSigningPrivateKey
  };
};

/**
 * Encrypts a message payload using Envelope Encryption (DEK + RSA-OAEP).
 * Used for both Learner and Admin chat.
 */
export const encryptMessageForRecipients = async (
  content: string,
  recipients: { userId: string, publicKey: string }[]
): Promise<{ encryptedContent: string, accessKeys: { userId: string, encryptedDEK: string }[] }> => {
  // 1. Generate a random AES-GCM DEK (Data Encryption Key)
  const dek = await window.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );

  // 2. Encrypt the content with the DEK
  const enc = new TextEncoder();
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    dek,
    enc.encode(content)
  );

  // Combine IV and Ciphertext for storing
  const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encryptedBuffer), iv.length);
  const encryptedContentBase64 = bufferToBase64(combined.buffer);

  // 3. Export the DEK to raw format so we can encrypt it with RSA
  const exportedDEK = await window.crypto.subtle.exportKey('raw', dek);

  // 4. Encrypt the DEK for each recipient using their RSA Public Key
  const accessKeys = await Promise.all(recipients.map(async (recp) => {
    const rsaPublicKey = await importPublicKey(recp.publicKey);
    const encryptedDEKBuffer = await window.crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      rsaPublicKey,
      exportedDEK
    );
    return {
      userId: recp.userId,
      encryptedDEK: bufferToBase64(encryptedDEKBuffer)
    };
  }));

  return {
    encryptedContent: encryptedContentBase64,
    accessKeys
  };
};

/**
 * Decrypts an envelope-encrypted message using the user's private key.
 */
export const decryptMessage = async (
  encryptedContent: string,
  encryptedDEK: string,
  privateKey: CryptoKey
): Promise<string> => {
  // 1. Decrypt the DEK using the RSA Private Key
  const dekBuffer = await window.crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    privateKey,
    base64ToBuffer(encryptedDEK)
  );

  // 2. Import the DEK back as an AES-GCM key
  const dek = await window.crypto.subtle.importKey(
    'raw',
    dekBuffer,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  // 3. Decrypt the content
  const combinedBuffer = base64ToBuffer(encryptedContent);
  const iv = new Uint8Array(combinedBuffer.slice(0, 12));
  const encryptedMaterial = combinedBuffer.slice(12);

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    dek,
    encryptedMaterial
  );

  return new TextDecoder().decode(decryptedBuffer);
};

// ── PILLAR 3: SHAMIR'S SECRET SHARING (SSS) ──────────────────────────────────

/**
 * Minimal GF(256) implementation for Shamir's Secret Sharing.
 */
const primitivePoly = 0x11d;
const gfLog = new Uint8Array(256);
const gfExp = new Uint8Array(256);

(function initGF() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    gfLog[x] = i;
    gfExp[i] = x;
    x <<= 1;
    if (x & 0x100) x ^= primitivePoly;
  }
  gfExp[255] = gfExp[0];
})();

const gfMul = (a: number, b: number) => (a === 0 || b === 0) ? 0 : gfExp[(gfLog[a] + gfLog[b]) % 255];
const gfDiv = (a: number, b: number) => (a === 0) ? 0 : gfExp[(gfLog[a] - gfLog[b] + 255) % 255];

/**
 * Evaluates a polynomial at x.
 */
const evalPoly = (poly: number[], x: number) => {
  let acc = 0;
  for (let i = poly.length - 1; i >= 0; i--) {
    acc = gfMul(acc, x) ^ poly[i];
  }
  return acc;
};

/**
 * Splits a byte array into N shares with threshold K.
 * Returns shares as hex strings (first byte is x-coordinate).
 */
export const splitSecret = (secret: Uint8Array, n: number, k: number): string[] => {
  const shares: string[][] = Array.from({ length: n }, (_, i) => [ (i + 1).toString(16).padStart(2, '0') ]);
  
  for (let b = 0; b < secret.length; b++) {
    const poly = [ secret[b] ];
    for (let i = 1; i < k; i++) {
      poly.push(window.crypto.getRandomValues(new Uint8Array(1))[0]);
    }
    for (let i = 1; i <= n; i++) {
      shares[i-1].push(evalPoly(poly, i).toString(16).padStart(2, '0'));
    }
  }
  
  return shares.map(s => s.join(''));
};

/**
 * Reconstitutes the secret from K shares.
 */
export const combineShares = (sharesHex: string[]): Uint8Array => {
  const shares = sharesHex.map(s => {
    const bytes = [];
    for (let i = 0; i < s.length; i += 2) bytes.push(parseInt(s.substr(i, 2), 16));
    return bytes;
  });
  
  const k = shares.length;
  const secretLen = shares[0].length - 1;
  const secret = new Uint8Array(secretLen);
  
  for (let b = 0; b < secretLen; b++) {
    let acc = 0;
    for (let i = 0; i < k; i++) {
      let num = 1;
      let den = 1;
      for (let j = 0; j < k; j++) {
        if (i === j) continue;
        num = gfMul(num, shares[j][0]);
        den = gfMul(den, shares[i][0] ^ shares[j][0]);
      }
      acc ^= gfMul(shares[i][b+1], gfDiv(num, den));
    }
    secret[b] = acc;
  }
  
  return secret;
};

/**
 * Encrypts a string (e.g. share) for a recipient using their RSA Public Key.
 */
export const encryptForUser = async (text: string, publicKeyBase64: string): Promise<string> => {
  const publicKey = await importPublicKey(publicKeyBase64);
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    new TextEncoder().encode(text)
  );
  return bufferToBase64(encryptedBuffer);
};

/**
 * Decrypts a string using the current user's RSA Private Key.
 */
export const decryptForUser = async (encryptedBase64: string, privateKey: CryptoKey): Promise<string> => {
  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    privateKey,
    base64ToBuffer(encryptedBase64)
  );
  return new TextDecoder().decode(decryptedBuffer);
};

// ── SYMMETRIC ENCRYPTION HELPERS (For Envelope Encryption/MasterKey) ─────────

/**
 * Encrypts data using a raw symmetric key (32 bytes).
 * Used for MasterKey envelope encryption.
 */
export const encryptWithSymmetricKey = async (data: Uint8Array, keyBytes: Uint8Array): Promise<string> => {
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    keyBytes as any,
    { name: 'AES-GCM' },
    false,
    ['encrypt']
  );

  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    data as any
  );

  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encrypted), iv.length);
  
  return bufferToBase64(combined.buffer);
};

/**
 * Decrypts data using a raw symmetric key (32 bytes).
 */
export const decryptWithSymmetricKey = async (encryptedBase64: string, keyBytes: Uint8Array): Promise<Uint8Array> => {
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    keyBytes as any,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  const combinedBuffer = base64ToBuffer(encryptedBase64);
  const iv = new Uint8Array(combinedBuffer.slice(0, 12));
  const encryptedMaterial = combinedBuffer.slice(12);

  const decrypted = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    encryptedMaterial
  );

  return new Uint8Array(decrypted);
};

// ── PILLAR 4: DIGITAL SIGNATURES ─────────────────────────────────────────────

/**
 * Signs a payload string with the user's RSA-PSS private key.
 */
export const signData = async (data: string, privateKey: CryptoKey): Promise<string> => {
  const enc = new TextEncoder();
  const signature = await window.crypto.subtle.sign(
    { name: 'RSA-PSS', saltLength: 32 },
    privateKey,
    enc.encode(data)
  );
  return bufferToBase64(signature);
};

/**
 * Verifies an RSA-PSS signature.
 */
export const verifyData = async (data: string, signatureBase64: string, publicKey: CryptoKey): Promise<boolean> => {
  const enc = new TextEncoder();
  return window.crypto.subtle.verify(
    { name: 'RSA-PSS', saltLength: 32 },
    publicKey,
    base64ToBuffer(signatureBase64),
    enc.encode(data)
  );
};
